import { makeAutoObservable } from 'mobx';
import IPasswordResetResponse from '../interfaces/IPasswordResetResponse';
import IUser from '../interfaces/IUser';
import IUserActivationRequest from '../interfaces/IUserActivationRequest';
import IUserActivationResponse from '../interfaces/IUserActivationResponse';
import IUserProfileSettingsRequest from '../interfaces/IUserProfileSettingsRequest';
import IUserSettings from '../interfaces/IUserSettings';
import AuthService from '../services/authService';
import IdentityService from '../services/identityService';
import LocalStorageService from '../services/localStorageService';

export default class AuthStore {
    isInitialAppLoading: boolean = true;
    isAuthenticated: boolean = false;
    isLoading: boolean = false;
    error: string | null = null;
    user: IUser | null = null;
    userSettings: IUserSettings | null = null;
    publicRegridAccessToken: string | null = null;
    publicMapboxAccessToken: string | null = null;
    publicGoogleAccessToken: string | null = null;

    constructor(
        private localStorageService: LocalStorageService,
        private authService: AuthService,
        private identityService: IdentityService,
    ) {
        makeAutoObservable(this);
    }

    setInitialAppLoaded() {
        this.isInitialAppLoading = false;
    }

    setIsAuthenticated(value: boolean) {
        this.isAuthenticated = value;
    }

    setIsLoading(value: boolean) {
        this.isLoading = value;
    }

    setError(value: string | null) {
        this.error = value;
    }

    setUser(value: IUser | null) {
        this.user = value;
    }

    setUserSettings(value: IUserSettings | null) {
        this.userSettings = value;
    }

    setPublicRegridAccessToken(value: string | null) {
        this.publicRegridAccessToken = value;
    }

    setPublicMapboxAccessToken(value: string | null) {
        this.publicMapboxAccessToken = value;
    }

    setPublicGoogleAccessToken(value: string | null) {
        this.publicGoogleAccessToken = value;
    }

    async login(login: string, password: string, rememberMe: boolean) {
        this.setIsLoading(true);
        try {
            const tokenPayload = await this.authService.login({
                login,
                password,
                rememberMe,
            });

            this.localStorageService.setTokenPayload(tokenPayload);
            await this.initializeInternal(tokenPayload.email);
        } catch (error: any) {
            this.setIsAuthenticated(false);
            if (error === "account_locked")
                this.setError("You have exceeded the total allowed failed logins. You can use the 'Forgot password' link or email us at 'contact-us@recity.co'.");
            else if (error === "invalid_grant")
                this.setError("Invalid email or password. Please re-enter.");
            else
                throw error;
        } finally {
            this.setIsLoading(false);
        }
    }

    async initialize() {
        const tokenPayload = this.localStorageService.getTokenPayload();
        if (!tokenPayload) {
            this.setInitialAppLoaded();
            return;
        }

        try {
            const newTokenPayload = await this.authService.refreshToken(tokenPayload.refresh_token);
            this.localStorageService.setTokenPayload(newTokenPayload);
            await this.initializeInternal(newTokenPayload.email);
        } catch {
            this.setIsAuthenticated(false);
        } finally {
            this.setInitialAppLoaded();
        }
    }

    async updateUserProfileSettings(request: IUserProfileSettingsRequest): Promise<void> {
        this.setIsLoading(true);
        try {
            await this.identityService.updateUserProfileSettings(request);
            this.setUser({
                ...this.user!,
                firstName: request.firstName,
                lastName: request.lastName,
            });

            this.setUserSettings({
                ...this.userSettings!,
                profileSettings: {
                    companyName: request.companyName,
                    title: request.title,
                },
            });
        } finally {
            this.setIsLoading(false);
        }
    }

    async requestPasswordReset(email: string): Promise<IPasswordResetResponse> {
        this.setIsLoading(true);
        try {
            return await this.identityService.requestPasswordReset(email);
        } finally {
            this.setIsLoading(false);
        }
    }

    async setNewPassword(email: string, password: string, resetToken: string) {
        this.setIsLoading(true);
        try {
            await this.identityService.setNewPassword(email, password, resetToken);
        } finally {
            this.setIsLoading(false);
        }
    }

    async changePassword(currentPassword: string, newPassword: string): Promise<void> {
        this.setIsLoading(true);
        try {
            await this.identityService.changePassword(currentPassword, newPassword);
        } finally {
            this.setIsLoading(false);
        }
    }

    async requestActivationLink(email: string): Promise<void> {
        this.setIsLoading(true);
        try {
            await this.identityService.requestActivationLink(email);
        } finally {
            this.setIsLoading(false);
        }
    }

    async getActivationData(token: string): Promise<IUserActivationResponse> {
        this.setIsLoading(true);
        try {
            return await this.identityService.getActivationData(token);
        } finally {
            this.setIsLoading(false);
        }
    }

    async sendActivationData(request: IUserActivationRequest): Promise<void> {
        this.setIsLoading(true);
        try {
            await this.identityService.sendActivationData(request);
        } finally {
            this.setIsLoading(false);
        }
    }

    logout() {
        this.localStorageService.removeTokenPayload();
        this.setIsAuthenticated(false);
        this.setIsLoading(false);
        this.setError(null);
        this.setUser(null);
        this.setUserSettings(null);
        this.setPublicRegridAccessToken(null);
        this.setPublicMapboxAccessToken(null);
        this.setPublicGoogleAccessToken(null);
    }

    private async initializeInternal(email: string) {
        const user = await this.identityService.getIdentity(email);
        const userSettings = await this.identityService.getUserSettings();

        this.setUser(user);
        this.setUserSettings(userSettings);
        this.setPublicRegridAccessToken(userSettings.publicRegridAccessToken);
        this.setPublicMapboxAccessToken(userSettings.publicMapboxAccessToken);
        this.setPublicGoogleAccessToken(userSettings.publicGoogleAccessToken);
        this.setIsAuthenticated(true);
        this.setError(null);

        this.localStorageService.setUserEmail(user.email);
        this.identityService.appLoad();
    }
}